import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Visibility,
  VisibilityOff,
  Facebook as FacebookIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  AutorenewRounded as AutorenewIcon,
} from '@mui/icons-material';
import { getPlatformName } from '../config/environment';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

const API_BASE_URL = getApiUrl();

const Settings: React.FC = () => {
  const [disparosOnboarding, setDisparosOnboarding] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Facebook Pixel states
  const [facebookPixelEnabled, setFacebookPixelEnabled] = useState<boolean>(false);
  const [facebookPixelId, setFacebookPixelId] = useState<string>('');
  const [facebookPixelAccessToken, setFacebookPixelAccessToken] = useState<string>('');
  const [facebookPixelEventName, setFacebookPixelEventName] = useState<string>('Lead');
  const [showAccessToken, setShowAccessToken] = useState<boolean>(false);
  const [savingFacebookPixel, setSavingFacebookPixel] = useState<boolean>(false);
  const [facebookPixelError, setFacebookPixelError] = useState<string>('');
  const [facebookPixelSuccess, setFacebookPixelSuccess] = useState<string>('');

  // Email SMTP states
  const [smtpHost, setSmtpHost] = useState<string>('smtp.hostinger.com');
  const [smtpPort, setSmtpPort] = useState<number>(465);
  const [smtpSecure, setSmtpSecure] = useState<boolean>(true);
  const [smtpUser, setSmtpUser] = useState<string>('');
  const [smtpPassword, setSmtpPassword] = useState<string>('');
  const [fromEmail, setFromEmail] = useState<string>('');
  const [fromName, setFromName] = useState<string>('Pooh Saúde');
  const [smtpActive, setSmtpActive] = useState<boolean>(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState<boolean>(false);
  const [savingSmtp, setSavingSmtp] = useState<boolean>(false);
  const [smtpError, setSmtpError] = useState<string>('');
  const [smtpSuccess, setSmtpSuccess] = useState<string>('');
  const [hasSmtpPassword, setHasSmtpPassword] = useState<boolean>(false);

  // Auto-assign attendants states
  const [autoAssignEnabled, setAutoAssignEnabled] = useState<boolean>(false);
  const [savingAutoAssign, setSavingAutoAssign] = useState<boolean>(false);
  const [autoAssignError, setAutoAssignError] = useState<string>('');
  const [autoAssignSuccess, setAutoAssignSuccess] = useState<string>('');
  const [activeAttendantsCount, setActiveAttendantsCount] = useState<number>(0);

  // Carregar configuração ao montar o componente
  useEffect(() => {
    loadSettings();
    loadFacebookPixelSettings();
    loadSmtpSettings();
    loadAutoAssignSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/settings/disparos-onboarding`);
      
      if (response.data.success) {
        setDisparosOnboarding(response.data.enabled);
      } else {
        setError('Erro ao carregar configuração');
      }
    } catch (err: any) {
      console.error('Erro ao carregar configuração:', err);
      setError('Erro ao carregar configuração');
      // Em caso de erro, manter o valor padrão (false)
      setDisparosOnboarding(false);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisparosOnboarding = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setDisparosOnboarding(newValue);
    setError('');
    setSuccess('');

    try {
      setSaving(true);
      const response = await axios.put(`${API_BASE_URL}/settings/disparos-onboarding`, {
        enabled: newValue
      });

      if (response.data.success) {
        setSuccess(`Disparos Onboarding ${newValue ? 'ativado' : 'desativado'} com sucesso!`);
        // Limpar mensagem de sucesso após 3 segundos
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Erro ao salvar configuração');
        // Reverter o estado em caso de erro
        setDisparosOnboarding(!newValue);
      }
    } catch (err: any) {
      console.error('Erro ao salvar configuração:', err);
      setError('Erro ao salvar configuração');
      // Reverter o estado em caso de erro
      setDisparosOnboarding(!newValue);
    } finally {
      setSaving(false);
    }
  };

  const loadFacebookPixelSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/settings/facebook-pixel`);
      
      if (response.data.success && response.data.data) {
        const config = response.data.data;
        setFacebookPixelEnabled(config.enabled || false);
        setFacebookPixelId(config.pixelId || '');
        setFacebookPixelAccessToken(config.accessToken || '');
        setFacebookPixelEventName(config.eventName || 'Lead');
      }
    } catch (err: any) {
      console.error('Erro ao carregar configurações do Facebook Pixel:', err);
      // Manter valores padrão em caso de erro
    }
  };

  const handleSaveFacebookPixel = async () => {
    setFacebookPixelError('');
    setFacebookPixelSuccess('');

    if (facebookPixelEnabled && (!facebookPixelId || !facebookPixelAccessToken)) {
      setFacebookPixelError('Pixel ID e Access Token são obrigatórios quando habilitado');
      return;
    }

    try {
      setSavingFacebookPixel(true);
      const response = await axios.put(`${API_BASE_URL}/settings/facebook-pixel`, {
        pixelId: facebookPixelId,
        accessToken: facebookPixelAccessToken,
        eventName: facebookPixelEventName,
        enabled: facebookPixelEnabled,
      });

      if (response.data.success) {
        setFacebookPixelSuccess('Configurações do Facebook Pixel salvas com sucesso!');
        setTimeout(() => setFacebookPixelSuccess(''), 3000);
      } else {
        setFacebookPixelError('Erro ao salvar configurações');
      }
    } catch (err: any) {
      console.error('Erro ao salvar configurações do Facebook Pixel:', err);
      setFacebookPixelError('Erro ao salvar configurações');
    } finally {
      setSavingFacebookPixel(false);
    }
  };

  const loadSmtpSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/settings/email-smtp`);
      
      if (response.data.success && response.data.data) {
        const config = response.data.data;
        setSmtpHost(config.smtp_host || 'smtp.hostinger.com');
        setSmtpPort(config.smtp_port || 465);
        setSmtpSecure(config.smtp_secure !== undefined ? config.smtp_secure : true);
        setSmtpUser(config.smtp_user || '');
        setFromEmail(config.from_email || '');
        setFromName(config.from_name || 'Pooh Saúde');
        setSmtpActive(config.is_active || false);
        setHasSmtpPassword(config.has_password || false);
      }
    } catch (err: any) {
      console.error('Erro ao carregar configurações SMTP:', err);
    }
  };

  const handleSaveSmtp = async () => {
    setSmtpError('');
    setSmtpSuccess('');

    if (!smtpUser || !fromEmail) {
      setSmtpError('Email completo e usuário SMTP são obrigatórios');
      return;
    }

    if (!smtpPassword && !hasSmtpPassword) {
      setSmtpError('Senha do email é obrigatória');
      return;
    }

    try {
      setSavingSmtp(true);
      const response = await axios.put(`${API_BASE_URL}/settings/email-smtp`, {
        smtp_host: smtpHost,
        smtp_port: smtpPort,
        smtp_secure: smtpSecure,
        smtp_user: smtpUser,
        smtp_password_encrypted: smtpPassword || undefined, // Só enviar se foi alterada
        from_email: fromEmail,
        from_name: fromName,
        is_active: smtpActive,
      });

      if (response.data.success) {
        setSmtpSuccess('Configurações de email salvas com sucesso!');
        setSmtpPassword(''); // Limpar campo de senha após salvar
        setHasSmtpPassword(true);
        setTimeout(() => setSmtpSuccess(''), 3000);
      } else {
        setSmtpError('Erro ao salvar configurações');
      }
    } catch (err: any) {
      console.error('Erro ao salvar configurações SMTP:', err);
      setSmtpError(err.response?.data?.error || 'Erro ao salvar configurações');
    } finally {
      setSavingSmtp(false);
    }
  };

  // =====================================================
  // AUTO-ATRIBUIÇÃO DE ATENDENTES
  // =====================================================

  const loadAutoAssignSettings = async () => {
    try {
      const [settingsResponse, attendantsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/settings/auto-assign-attendants`),
        axios.get(`${API_BASE_URL}/attendants?active_only=true`),
      ]);

      if (settingsResponse.data.success) {
        setAutoAssignEnabled(settingsResponse.data.enabled);
      }

      if (attendantsResponse.data.success) {
        setActiveAttendantsCount(attendantsResponse.data.count || 0);
      }
    } catch (err: any) {
      console.error('Erro ao carregar configuração de auto-atribuição:', err);
    }
  };

  const handleToggleAutoAssign = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setAutoAssignEnabled(newValue);
    setAutoAssignError('');
    setAutoAssignSuccess('');

    try {
      setSavingAutoAssign(true);
      const response = await axios.put(`${API_BASE_URL}/settings/auto-assign-attendants`, {
        enabled: newValue,
      });

      if (response.data.success) {
        setAutoAssignSuccess(
          `Auto-atribuição ${newValue ? 'ativada' : 'desativada'} com sucesso!`
        );
        setTimeout(() => setAutoAssignSuccess(''), 3000);
      } else {
        setAutoAssignError(response.data.error || 'Erro ao salvar configuração');
        setAutoAssignEnabled(!newValue);
      }
    } catch (err: any) {
      console.error('Erro ao salvar configuração de auto-atribuição:', err);
      setAutoAssignError(err.response?.data?.error || 'Erro ao salvar configuração');
      setAutoAssignEnabled(!newValue);
    } finally {
      setSavingAutoAssign(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          Configurações
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gerencie configurações gerais da {getPlatformName()}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Configurações Gerais
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Disparos Onboarding
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Controla se os disparos de onboarding estão ativados.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={disparosOnboarding}
                      onChange={handleToggleDisparosOnboarding}
                      disabled={saving}
                      color="primary"
                    />
                  }
                  label={disparosOnboarding ? 'ATIVADO' : 'DESATIVADO'}
                  sx={{ ml: 2 }}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Auto-atribuição de Atendentes Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <PeopleIcon sx={{ fontSize: 32, color: '#7C3AED' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Auto-Atribuição de Atendentes
            </Typography>
            <Chip
              icon={<AutorenewIcon />}
              label="Round-Robin"
              size="small"
              sx={{ bgcolor: '#7C3AED22', color: '#7C3AED', fontWeight: 600 }}
            />
          </Box>

          {autoAssignError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAutoAssignError('')}>
              {autoAssignError}
            </Alert>
          )}

          {autoAssignSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAutoAssignSuccess('')}>
              {autoAssignSuccess}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Atribuição Automática
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quando ativado, cada novo chat será automaticamente atribuído a um atendente usando o sistema round-robin (distribuição equilibrada).
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  label={`${activeAttendantsCount} atendente${activeAttendantsCount !== 1 ? 's' : ''} ativo${activeAttendantsCount !== 1 ? 's' : ''}`}
                  size="small"
                  color={activeAttendantsCount > 0 ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={autoAssignEnabled}
                  onChange={handleToggleAutoAssign}
                  disabled={savingAutoAssign}
                  color="primary"
                />
              }
              label={autoAssignEnabled ? 'ATIVADO' : 'DESATIVADO'}
              sx={{ ml: 2 }}
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Como funciona:</strong> O sistema distribui novos chats de forma equilibrada entre todos os atendentes ativos. 
              Exemplo: se há 3 atendentes (A, B, C), o 1º chat vai para A, o 2º para B, o 3º para C, o 4º volta para A, e assim por diante.
              Chats já existentes não são afetados. A atribuição automática só se aplica a novas conversas.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Facebook Pixel Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <FacebookIcon sx={{ fontSize: 32, color: '#1877F2' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Facebook Pixel
            </Typography>
          </Box>

          {facebookPixelError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFacebookPixelError('')}>
              {facebookPixelError}
            </Alert>
          )}

          {facebookPixelSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFacebookPixelSuccess('')}>
              {facebookPixelSuccess}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={facebookPixelEnabled}
                  onChange={(e) => setFacebookPixelEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Habilitar Facebook Pixel
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Envia eventos automaticamente ao Facebook Pixel sempre que um novo lead é criado
                  </Typography>
                </Box>
              }
            />
          </Box>

          {facebookPixelEnabled && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                <TextField
                  label="Pixel ID"
                  value={facebookPixelId}
                  onChange={(e) => setFacebookPixelId(e.target.value)}
                  fullWidth
                  placeholder="Ex: 123456789012345"
                  helperText="ID do seu Facebook Pixel (encontrado no Gerenciador de Eventos do Facebook)"
                />

                <TextField
                  label="Access Token"
                  value={facebookPixelAccessToken}
                  onChange={(e) => setFacebookPixelAccessToken(e.target.value)}
                  fullWidth
                  type={showAccessToken ? 'text' : 'password'}
                  placeholder="Ex: EAAxxxxxxxxxxxxx"
                  helperText="Token de acesso do Facebook (gerado no Gerenciador de Eventos)"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowAccessToken(!showAccessToken)}
                          edge="end"
                        >
                          {showAccessToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Nome do Evento"
                  value={facebookPixelEventName}
                  onChange={(e) => setFacebookPixelEventName(e.target.value)}
                  fullWidth
                  placeholder="Ex: Lead"
                  helperText="Nome do evento que será enviado ao Facebook Pixel (padrão: Lead)"
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleSaveFacebookPixel}
                disabled={savingFacebookPixel}
                sx={{ bgcolor: '#1877F2', '&:hover': { bgcolor: '#166FE5' } }}
              >
                {savingFacebookPixel ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email SMTP Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Configurações de Email
            </Typography>
          </Box>

          {smtpError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSmtpError('')}>
              {smtpError}
            </Alert>
          )}

          {smtpSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSmtpSuccess('')}>
              {smtpSuccess}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <TextField
              label="Servidor SMTP"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              fullWidth
              placeholder="smtp.hostinger.com"
              helperText="Servidor SMTP do seu provedor de email"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Porta SMTP"
                value={smtpPort}
                onChange={(e) => setSmtpPort(parseInt(e.target.value) || 465)}
                type="number"
                sx={{ width: '200px' }}
                helperText="Porta do servidor SMTP (465 para SSL, 587 para TLS)"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={smtpSecure}
                    onChange={(e) => setSmtpSecure(e.target.checked)}
                    color="primary"
                  />
                }
                label="Conexão Segura (SSL/TLS)"
                sx={{ mt: 2 }}
              />
            </Box>

            <TextField
              label="Email Completo"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              fullWidth
              placeholder="noreply@seudominio.com"
              helperText="Email completo configurado no Hostinger (ex: noreply@seudominio.com)"
              required
            />

            <TextField
              label="Senha do Email"
              value={smtpPassword}
              onChange={(e) => setSmtpPassword(e.target.value)}
              fullWidth
              type={showSmtpPassword ? 'text' : 'password'}
              placeholder={hasSmtpPassword ? '••••••••' : 'Digite a senha'}
              helperText="Senha configurada ao criar o email no Hostinger (não a senha do painel)"
              required={!hasSmtpPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      edge="end"
                    >
                      {showSmtpPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Email Remetente"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              fullWidth
              placeholder="noreply@seudominio.com"
              helperText="Email que aparecerá como remetente (geralmente o mesmo do usuário SMTP)"
              required
            />

            <TextField
              label="Nome do Remetente"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              fullWidth
              placeholder="Sistema CRM"
              helperText="Nome que aparecerá como remetente nos emails"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={smtpActive}
                  onChange={(e) => setSmtpActive(e.target.checked)}
                  color="primary"
                />
              }
              label="Ativar envio de emails"
              sx={{ mt: 1 }}
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleSaveSmtp}
            disabled={savingSmtp}
            startIcon={<EmailIcon />}
          >
            {savingSmtp ? 'Salvando...' : 'Salvar Configurações de Email'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings; 